import React from "react";
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import StatusBadgeCst from "@components/StatusBadgeCst";
import { EnrolledUserItem } from "../types";
import { formatEnrollTypeLabel } from "../config/enrollTypeMap";

interface UserEnrollmentRowProps {
  user: EnrolledUserItem;
  onArrowClick?: (user: EnrolledUserItem) => void;
}

const NeutralBadge: React.FC<{ label: string }> = ({ label }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        padding: "4px 10px",
        borderRadius: "8px",
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        fontSize: "12px",
        fontWeight: 600,
        color: theme.palette.text.primary,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
  );
};

const UserEnrollmentRow: React.FC<UserEnrollmentRowProps> = ({
  user,
  onArrowClick,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("users-and-groups");
  const fullName =
    [user.userFirstName, user.userLastName].filter(Boolean).join(" ").trim() ||
    "-";
  const enrollLabel = formatEnrollTypeLabel(user.enrollType);
  const isActive = (user.status || "").toUpperCase() !== "DELETED";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        padding: "14px 16px",
        borderRadius: "10px",
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        [theme.breakpoints.down("lg")]: {
          flexDirection: "column",
          alignItems: "stretch",
          gap: 1.5,
        },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 700,
            color: theme.palette.text.primary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {fullName}
        </Typography>
        <Typography
          sx={{
            fontSize: "12px",
            color: theme.palette.text.tertiary,
            lineHeight: 1.4,
          }}
        >
          {`ID: ${user.userId ?? "-"}${
            user.userEmail ? ` | ${user.userEmail}` : ""
          }`}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexShrink: 0,
          [theme.breakpoints.down("lg")]: {
            justifyContent: "flex-start",
            flexWrap: "wrap",
          },
        }}
      >
        {enrollLabel && <NeutralBadge label={enrollLabel} />}
        {isActive && (
          <StatusBadgeCst label={String(t("row.active"))} variant="active" />
        )}

        <IconButton
          size="small"
          onClick={() => onArrowClick?.(user)}
          sx={{ color: theme.palette.primary.main }}
          aria-label="open detail"
        >
          <ArrowRight size={18} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default UserEnrollmentRow;
